import { useEffect } from 'react';

import {
  ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT,
  ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT,
  ZOOM_CHAT_IDENTITY_GRACE_MS,
  ZOOM_CHAT_SDK_READY_TIMEOUT_MS
} from './zoomChat.constants';
import { onZoomChatSdkAvailable } from './zoomChatSdk';
import { createTraceId, reportIdentityOutcome } from './zoomChatTelemetry';

type CredentialRequest = {
  issued: boolean;
  requestedAt: number;
};

type Engagement = {
  requests: CredentialRequest[];
};

/**
 * What this document's Zoom client has asked the credential hook for, kept by who owns each request.
 * A request is a record of its own and the hook hands it back with its result, so a result is
 * credited to the request it answers and to nothing else. A flag that lives for the whole document
 * is set by the first credential and then excuses every later chat, and so does one timestamp for
 * the latest identity when two chats start close together.
 *
 * Every request has one owner, fixed when the request is made or when the engagement that owns it
 * starts, never when the engagement is judged, so a timer that fires late cannot hand it to the
 * wrong chat:
 *
 * - While an engagement is open, from its start event to its end event or to the start of the next
 *   engagement, every request is its own. That covers a repeat ask, a retry after a refusal and an
 *   answer that comes late, so none of them can excuse the next chat.
 * - With none open, a request waits unclaimed, and the engagement that starts within a grace period
 *   takes all that wait. Zoom may ask for the credential as part of starting the chat and the order
 *   is its to choose. A request older than that belongs to no one, and is dropped when the next
 *   request is made or an engagement starts.
 *
 * Module scope is the right granularity, not a shortcut: the same reasoning as `evictionOwed` in
 * clearZoomChatIdentity.ts. What this answers is about THIS DOCUMENT's dealings with the SDK, and
 * the SDK's lifetime is exactly this document's: it is an ES module and cannot be unloaded, so it
 * outlives every mount and unmount of the component that injected it. A ref would reset while the
 * client it describes kept running.
 *
 * It cannot carry one patient's answer into another patient's chat. A replacement either reloads
 * the document, which resets this module with it, or fails the claim closed, and in between the
 * issue-time boundary refuses to mint at all.
 */
let unclaimedRequests: CredentialRequest[] = [];
let openEngagement: Engagement | undefined;

const dropStaleRequests = (now: number) => {
  unclaimedRequests = unclaimedRequests.filter(
    ({ requestedAt }) => requestedAt >= now - ZOOM_CHAT_IDENTITY_GRACE_MS
  );
};

const beginEngagement = (startedAt: number): Engagement => {
  dropStaleRequests(startedAt);

  const engagement: Engagement = { requests: unclaimedRequests };

  unclaimedRequests = [];
  openEngagement = engagement;

  return engagement;
};

const endOpenEngagement = () => {
  openEngagement = undefined;
};

/**
 * Recorded on ENTRY to the credential hook, before any of its own guards run. The distinction the
 * watchdog needs is "Zoom asked" versus "Zoom never asked": a request that our own guards then
 * refuse is a failure we already log by reason, whereas a request that never arrives is invisible
 * and is the failure this whole mechanism exists to surface.
 *
 * Returns the record of the request, which the hook hands back to `recordZoomChatCredentialIssued`
 * so a result is credited to the request it answers, not to whichever engagement is open when it
 * lands. Two requests in the same millisecond are still two records, which a time would not be.
 */
export const recordZoomChatCredentialRequested = (): CredentialRequest => {
  const request: CredentialRequest = { issued: false, requestedAt: Date.now() };

  if (openEngagement) {
    openEngagement.requests.push(request);
  } else {
    dropStaleRequests(request.requestedAt);
    unclaimedRequests.push(request);
  }

  return request;
};

/**
 * Recorded only where a token is actually returned to Zoom, the sole point identity takes hold, and
 * on the request it answers. It changes that request and no other, so an answer that arrives late,
 * after the engagement that owns the request has been judged or the next one has started, cannot
 * count for anything else.
 */
export const recordZoomChatCredentialIssued = (request: CredentialRequest) => {
  request.issued = true;
};

/**
 * Reports the two silent failures the SDK's own error handling cannot reach.
 *
 * `useScript`'s error covers the transport — Zoom's CDN unreachable, the script blocked — and
 * nothing else. Both of these leave the script returning 200 and loading cleanly, so no error event
 * ever fires, while the impression event still reports the channel healthy:
 *
 * 1. A dark surface. A revoked apikey, a host matching no Campaign Location, or a failed dynamic
 *    import of the campaign runtime all end with the loader executed and no chat. Detected by the
 *    runtime never publishing its API, or publishing it and never reaching `isReady`, which are
 *    logged as separate reasons because they point at different causes: the first at campaign
 *    resolution, the second at chat/video init after it.
 *
 * 2. An engagement with no verified patient behind it. This is the one that matters most, because
 *    it is a correctness failure rather than an availability one: chat works, the patient sees a
 *    normal conversation, and the agent is quietly serving someone unidentified. That is precisely
 *    the state Consumer Authentication was added to eliminate, so it must not be able to happen
 *    unobserved.
 *
 * Both are warn-level logs and change nothing the patient sees. That is deliberate — tearing a
 * working chat down because identity is missing would turn a degraded conversation into no
 * conversation, and the patient is mid-sentence with an agent by the time we know.
 *
 * The second is judged per engagement, by the credential requests it owns (see
 * `unclaimedRequests`). Zoom asks for a credential when an engagement begins, so an engagement owns
 * what was asked from one grace period before its start event until its end event, or until the
 * next engagement starts when the end is not seen. It is judged at the end of its own grace period
 * and is healthy when any request it owns was answered with an identity. None owned is
 * 'engagement-without-credential-request'. Some owned and none answered is
 * 'engagement-without-verified-identity'. A request has one owner and an answer counts only for the
 * request it answers, so an earlier chat's credential, repeat ask, retry or late answer cannot
 * excuse a later chat, however close together the two start. The window opens before the event
 * because the SDK may ask for the credential as part of starting the engagement and the order is
 * its to choose, so a request made just before the event is still this engagement's.
 *
 * What it cannot know is which request Zoom meant for which chat, because the callback says nothing
 * about it. So:
 *
 * - A chat is judged on its requests together. When its own request was refused but Zoom's start-up
 *   request, made within a grace period before it, was answered, the chat is not logged here. The
 *   refusal is already logged by its own reason.
 * - It relies on `engagement_ended`. When the end of a chat is not seen, the next chat's start is
 *   what closes it, so a request Zoom made for the next chat just before that start is still the
 *   earlier chat's, and the next chat is logged as never asked.
 * - Each start event is judged as an engagement of its own, whatever its payload says. Zoom's
 *   runtime announces a chat it restores as a new start, with `restored: true`, when its chat client
 *   comes up on a live chat. After a reload during one, that chat is logged unless Zoom asks for a
 *   credential again.
 * - A credential Zoom made more than a grace period before an engagement and then reuses for it is
 *   not seen, so that chat is logged although it was identified. Whether Zoom reuses one has not
 *   been established.
 *
 * A clean log is not proof that every chat was verified; the agent's badge is.
 *
 * It is armed by `ZoomChatSdkLoader`, so it starts once the SDK script is injected or already in
 * the document, not before. App's loader and a session change can unmount it, which drops its
 * timers and closes the open engagement, since its end can no longer be seen. An engagement still
 * inside its grace period is then never judged, and a later mount starts the readiness clock again.
 */
export const useZoomChatIdentityWatchdog = () => {
  useEffect(() => {
    // One list, cleared wholesale on unmount. Grace timers are appended as engagements begin, so
    // they must share the array the cleanup closes over rather than be tracked individually.
    const pendingTimers: ReturnType<typeof setTimeout>[] = [];
    const detachEngagementListeners: (() => void)[] = [];

    pendingTimers.push(
      setTimeout(() => {
        const sdk = window.zoomCampaignSdk;

        if (sdk?.isReady) {
          return;
        }

        reportIdentityOutcome({
          level: 'warn',
          reason: sdk ? 'sdk-never-ready' : 'sdk-runtime-never-published',
          traceId: createTraceId()
        });
      }, ZOOM_CHAT_SDK_READY_TIMEOUT_MS)
    );

    const stopWaitingForSdk = onZoomChatSdkAvailable((sdk) => {
      const handleEngagementStarted = () => {
        // Claimed when the event arrives, not when the grace period ends: the window reaches back
        // from the event, and a timer that fires late must not take what was asked for the next
        // chat.
        const { requests } = beginEngagement(Date.now());

        pendingTimers.push(
          setTimeout(() => {
            if (requests.some(({ issued }) => issued)) {
              return;
            }

            reportIdentityOutcome({
              level: 'warn',
              // Two different bugs, so two different reasons. "Requested but never issued" is ours
              // — an endpoint, session or boundary failure, already logged in detail alongside
              // this. "Never requested" is Zoom's side of the contract not happening at all, and
              // has no other trace anywhere.
              reason:
                requests.length > 0
                  ? 'engagement-without-verified-identity'
                  : 'engagement-without-credential-request',
              traceId: createTraceId()
            });
          }, ZOOM_CHAT_IDENTITY_GRACE_MS)
        );
      };

      try {
        sdk.on(ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT, handleEngagementStarted);
        detachEngagementListeners.push(() =>
          sdk.off(ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT, handleEngagementStarted)
        );
        sdk.on(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, endOpenEngagement);
        detachEngagementListeners.push(() =>
          sdk.off(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, endOpenEngagement)
        );
      } catch {
        // A vendor emitter that rejects a subscription must not take the chat down with it. The
        // readiness check above still reports, so the surface is not left entirely unobserved.
      }
    });

    return () => {
      pendingTimers.forEach(clearTimeout);
      stopWaitingForSdk();
      // Its end can no longer be seen from here, so the engagement is not left open to take what a
      // later mount's chat asks for.
      endOpenEngagement();

      detachEngagementListeners.forEach((detach) => {
        try {
          detach();
        } catch {
          // Unsubscribing is best effort. A listener left behind would judge every engagement a
          // second time and log a chat that Zoom did ask for as never asked, a warning and nothing
          // more.
        }
      });
    };
  }, []);
};
